import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, List} from '@tickchak/react-share/app';
import Break from '../../../../../assets/icons/Break';
import PlusThin from '../../../../../assets/icons/PlusThin';
import XIcon from '../../../../../assets/icons/XIcon';
import useShow from '../../../../../hooks/useShow';
import useCloseOnScroll from '../../../../../hooks/useCloseOnScroll';
import * as css from './ActionsBar.css';

interface ActionsBarProps {
    onAddItem: () => void;
    onAddBreak: () => void;
}

const DROPDOWN_WIDTH = 240;
const DROPDOWN_GAP = 8;

function getCenteredAbovePosition(ref: React.RefObject<HTMLElement>) {
    if(!ref.current) return undefined;
    const rect = ref.current.getBoundingClientRect();
    return {
        bottom: window.innerHeight - rect.top + DROPDOWN_GAP,
        left: rect.left + rect.width / 2 - DROPDOWN_WIDTH / 2,
    };
}

function ActionsBar({onAddItem, onAddBreak}: ActionsBarProps) {
    const {t} = useTranslation('timetable');
    const show = useShow();
    const triggerRef = useRef<HTMLSpanElement>(null);
    useCloseOnScroll(show.isOpen, show.close);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        show.isOpen ? show.close() : show.open();
    };

    const handleItemClick = ({id}: {id: string | number}) => {
        show.close();
        if(id === 'lecture') onAddItem();
        else if(id === 'break') onAddBreak();
    };

    return (
        <css.actionsBar>
            <span ref={triggerRef}>
                <Button variant="brand" size="chip" onClick={toggle} ariaHaspopup="menu" ariaExpanded={show.isOpen}>{t('addItemGrid')}</Button>
            </span>
            <Button variant="neutral" size="chip" iconStart={<Break />} onClick={onAddBreak}>{t('addBreak')}</Button>
            {show.isOpen && (
                <List
                    fixedPosition={getCenteredAbovePosition(triggerRef)}
                    show={show}
                    onItemClick={handleItemClick}
                    position={{width: `${DROPDOWN_WIDTH}px`, height: 'fit-content', minHeight: 'fit-content'}}
                >
                    <List.Items>
                        <List.Item.Item id="lecture" text={t('addLecture')} startIcon={<PlusThin />} />
                        <List.Item.Item id="break"   text={t('addBreak')}   startIcon={<Break />} />
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('close')} iconStart={<XIcon />} size="category" />
                    </List.Footer>
                </List>
            )}
        </css.actionsBar>
    );
}

export default ActionsBar;
